import { ReactNode, RefObject } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export const editorControlStyles = {
  '& .MuiOutlinedInput-root': {
    minHeight: 42,
    bgcolor: '#FFFFFF',
    borderRadius: 1.5,
    '& fieldset': { borderColor: '#D8E1EF' },
    '&:hover fieldset': { borderColor: '#AAB9CF' },
    '&.Mui-focused fieldset': { borderColor: '#3B4797', borderWidth: 1 },
    '&.Mui-disabled': { bgcolor: '#F5F7FA' },
  },
};

export function EditorSectionCard({
  icon,
  title,
  description,
  badge,
  children,
  sectionRef,
  id,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  badge?: string;
  children: ReactNode;
  sectionRef?: RefObject<HTMLDivElement>;
  id?: string;
}) {
  return (
    <Paper
      ref={sectionRef}
      id={id}
      variant='outlined'
      sx={{
        p: { xs: 1.5, md: 2 },
        borderColor: '#E1E8F2',
        borderRadius: 2,
        scrollMarginTop: 24,
      }}
    >
      <Stack direction='row' spacing={1.75} alignItems='flex-start'>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: '#EEF0FA',
            color: '#3B4797',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Stack flex={1} minWidth={0}>
          <Stack direction='row' alignItems='center' spacing={1} flexWrap='wrap' useFlexGap>
            <Typography fontSize={16} fontWeight={800} color='#28356D'>
              {title}
            </Typography>
            {badge && (
              <Chip
                label={badge}
                size='small'
                sx={{ height: 22, bgcolor: '#F0FDF8', color: '#217A5C', fontWeight: 700, fontSize: 11 }}
              />
            )}
          </Stack>
          <Typography color='#667085' fontSize={13} lineHeight={1.55} mt={0.25}>
            {description}
          </Typography>
        </Stack>
      </Stack>
      <Box mt={2.5}>{children}</Box>
    </Paper>
  );
}

export function EditorField({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <Stack spacing={0.75} minWidth={0}>
      <Stack direction='row' spacing={0.5} alignItems='center'>
        <Typography color='#344054' fontSize={13} fontWeight={700}>
          {label}
        </Typography>
        {required && (
          <Typography component='span' color='#D92D20' fontSize={13} fontWeight={800}>
            *
          </Typography>
        )}
      </Stack>
      {children}
      {hint && (
        <Typography color='#7B8798' fontSize={12} lineHeight={1.4}>
          {hint}
        </Typography>
      )}
    </Stack>
  );
}
